/**
 * Lobby sub-state machine.
 *
 * States: parsing -> loading -> previewing -> joining -> in_call
 * Reset path: any state -> parsing
 */

import { createStore } from './store'
import type { LobbyPhase, LobbyState } from '../types'

/** Valid transitions: from -> set of allowed targets */
const VALID_TRANSITIONS: Record<LobbyPhase, readonly LobbyPhase[]> = {
  parsing: ['loading'],
  loading: ['previewing', 'parsing'],
  previewing: ['joining', 'parsing'],
  joining: ['in_call', 'parsing'],
  in_call: ['parsing'],
}

function canTransition(from: LobbyPhase, to: LobbyPhase): boolean {
  return VALID_TRANSITIONS[from].includes(to)
}

const INITIAL_STATE: LobbyState = {
  phase: 'parsing',
  meetingCode: null,
  peerHints: null,
  cameraReady: false,
  micReady: false,
  error: null,
}

export const lobbyStore = createStore<LobbyState>(INITIAL_STATE)

function transition(to: LobbyPhase, patch?: Partial<LobbyState>): void {
  const current = lobbyStore.getState().phase
  if (!canTransition(current, to)) {
    console.warn(`[lobby] invalid transition: ${current} -> ${to}`)
    return
  }
  lobbyStore.setState({ ...patch, phase: to })
}

/** URL parsed and code validated: parsing -> loading */
export function lobbyCodeParsed(
  meetingCode: string,
  peerHints?: unknown[] | null,
): void {
  transition('loading', {
    meetingCode,
    peerHints: peerHints ?? null,
    error: null,
  })
}

/** Media acquired or skipped: loading -> previewing */
export function lobbyMediaReady(camera: boolean, mic: boolean): void {
  transition('previewing', {
    cameraReady: camera,
    micReady: mic,
    error: null,
  })
}

/** User clicks Join: previewing -> joining */
export function lobbyJoin(): void {
  transition('joining', { error: null })
}

/** Connection established: joining -> in_call */
export function lobbyConnected(): void {
  transition('in_call', { error: null })
}

/** Reset lobby to initial state: any -> parsing */
export function lobbyReset(): void {
  lobbyStore.setState({ ...INITIAL_STATE })
}

/** Update media readiness without changing phase */
export function lobbySetMediaReady(camera: boolean, mic: boolean): void {
  lobbyStore.setState({ cameraReady: camera, micReady: mic })
}

/** Set an error without changing phase */
export function lobbySetError(error: string): void {
  lobbyStore.setState({ error })
}

/** Get current lobby phase */
export function getLobbyPhase(): LobbyPhase {
  return lobbyStore.getState().phase
}
